/* oxlint-disable @typescript-eslint/no-explicit-any */
import {Spec} from 'immutability-helper';
import $ from 'jquery';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import * as context from 'Context';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import AppDispatcher from 'lib/Dispatcher';
import SubtemplateDAO, {SubtemplatesByVersion} from 'notebook/entry-templates/SubtemplateDAO';
import SubtemplateUtil from 'notebook/entry-templates/SubtemplateUtil';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {ACTION_RESET_VERSION} from 'notebook/store/VersioningConstants';
import {MultidocTableOpMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {BioentityNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import type {Response} from 'notifications';
import pick from 'util/pick';
import {asJQueryPromise} from 'util/Promises';

import SubtemplateStore from './SubtemplateStore';
import {FromServerSubtemplate, Subtemplate, ToServerSubtemplate} from './TemplateConstants';

export interface UpdateSubtemplateOptions {
  cidIdMap?: CidIdMap | null;
  bumpVersion?: boolean | null;
  performTableEvaluation?: boolean;
  multidocTableOp?: MultidocTableOpMap;
}

const SubtemplateActions = {
  print: (
    subtemplateId: string,
    param: {indexSlice?: {start: number; end: number} | null; showNote: boolean; showTimestamps: boolean}
  ): void => {
    const showNote = param.showNote ?? false;
    const showTimestamps = param.showTimestamps ?? false;
    const {indexSlice} = param;
    const SubtemplateStore = require('notebook/entry-templates/SubtemplateStore').default;
    const state = SubtemplateStore.getEditorState(subtemplateId);

    const versionId = state.openVersionId;
    let args = `versionId=${versionId}`;
    if (showNote) {
      args += '&note=true';
    }
    if (indexSlice != null) {
      args += `&startIndex=${indexSlice.start}&endIndex=${indexSlice.end}`;
    }
    if (showTimestamps) {
      args += `&showTimestamps=${showTimestamps}`;
    }

    window.open(`${window.location.pathname}/view?${args}#print`, '_blank');
  },

  registerObjects(subtemplateId: string, cidIdMap: CidIdMap): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_REGISTER_OBJECTS,
      subtemplateId,
      cidIdMap,
    });
  },

  updateNote(
    subtemplateId: string,
    noteVersion: BioentityNote,
    structuredTableMappings?: BioentityNote['structuredTableMappings'],
    options: UpdateSubtemplateOptions = {}
  ): void {
    SubtemplateActions.update(
      subtemplateId,
      SubtemplateUtil.noteVersionUpdate({
        $merge: {...noteVersion, ...(structuredTableMappings && {structuredTableMappings})},
      }),
      {performTableEvaluation: options.performTableEvaluation, multidocTableOp: options.multidocTableOp}
    );
  },

  setName(subtemplateId: string, name: string): void {
    const {subtemplate} = SubtemplateStore.getEditorState(subtemplateId);
    SubtemplateActions.update(subtemplateId, {$merge: {name}}, {performTableEvaluation: false});

    writeFragmentsToCache(getFragmentWrapper('SubtemplateSummary'), [
      {
        __typename: 'SubtemplateNode',
        ...subtemplate,
        templateCollection: {
          __typename: 'TemplateCollectionNode',
          ...subtemplate.templateCollection,
        },
        editUrl: subtemplate.editURL,
        archiveRecord: null,
        creator: {__typename: 'UserNode', id: subtemplate.creatorId, name: subtemplate.creator},
      },
    ]);
  },

  /**
   * Applies `op` to the sub-template with id `subtemplateId`, and merges in the specified `cidIdMap`.
   * If `op` introduces new models, it should specify the ids in `cidIdMap`.
   */
  update(subtemplateId: string, op: Spec<Subtemplate>, param?: UpdateSubtemplateOptions): void {
    if (param == null) {
      param = {cidIdMap: null, bumpVersion: null};
    }
    const {cidIdMap, bumpVersion} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_UPDATE,
      subtemplateId,
      op,
      cidIdMap: cidIdMap != null ? cidIdMap : {},
      bumpVersion: bumpVersion != null ? bumpVersion : true,
      performTableEvaluation: param.performTableEvaluation,
      multidocTableOp: param.multidocTableOp,
    });
  },

  revert(subtemplateId: string, op: any, restoreData: any): void {
    // Replace the open sub-template with the specified data.
    // Useful to reset the state to an older state.
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_REVERT,
      subtemplateId,
      op,
      restoreData,
      multidocTableOp: {[subtemplateId]: 'eval-document'},
    });
  },

  // TODO(T1135): Don't pass in deferred. Note: Only ProxyFile should be passing in deferred.
  loadVersion(subtemplateId: string, versionId: number, param?: any): void {
    if (param == null) {
      param = {};
    }
    const {deferred} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_LOAD_VERSION,
      subtemplateId,
      versionId,
    });
    SubtemplateDAO.getVersion(subtemplateId, versionId).then(
      (response: FromServerSubtemplate) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SUBTEMPLATE_LOAD_VERSION_COMPLETED,
          subtemplateId,
          versionId,
          version: response,
        });
        if (deferred != null) {
          deferred.resolve();
        }
      },
      (response: Response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SUBTEMPLATE_LOAD_VERSION_FAILED,
          subtemplateId,
          versionId,
        });
        if (deferred != null) {
          deferred.reject(response);
        }
        notifications.notifyError(response);
      }
    );
  },

  fetchVersions(subtemplateId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_FETCH_VERSIONS,
      subtemplateId,
    });
    SubtemplateDAO.getVersions(subtemplateId).then(
      (response: SubtemplatesByVersion) => {
        AppDispatcher.dispatch(
          // eslint-disable-next-line no-restricted-syntax
          _.extend(
            {
              actionType: EventConstants.SUBTEMPLATE_FETCH_VERSIONS_COMPLETED,
              subtemplateId,
              versions: response.data,
            },
            pick(response, 'parent')
          )
        ); // parent key present with null value: the parent was deleted
        // omitted parent key: there is no parent.
      },
      (response: Response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SUBTEMPLATE_FETCH_VERSIONS_FAILED,
          subtemplateId,
        });
      }
    );
  },

  delete(subtemplateId: string, param: any): void {
    if (param == null) {
      param = {};
    }
    const {success} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_DELETE,
      subtemplateId,
    });
    SubtemplateDAO.delete(subtemplateId).then(
      (response: Subtemplate) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SUBTEMPLATE_DELETE_COMPLETED,
          subtemplateId,
        });
        if (typeof success === 'function') {
          success();
        }
      },
      (response: Response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SUBTEMPLATE_DELETE_FAILED,
          subtemplateId,
          error: response,
        });
        notifications.notifyError(response);
      }
    );
  },

  /**
   * Update non-versioned fields (name, template collection) via an immediate request that is
   * processed non-optimistically.
   *
   * @param  {String} subtemplateId
   * @param  {Object} An object containing:
   *                    data: {name, templateCollection}
   *                    success: an optional callback fired on success
   */
  async saveFileData(subtemplateId: string, {data, success}: any): Promise<void> {
    try {
      const subtemplate = await SubtemplateDAO.update(subtemplateId, data);
      const {name, templateCollection, editURL} = subtemplate;
      const mergeData = {name, templateCollection, editURL};
      if (data.name != null) {
        mergeData.name = data.name;
      }
      SubtemplateActions.update(
        subtemplateId,
        {$merge: mergeData},
        {bumpVersion: false, cidIdMap: null, performTableEvaluation: false}
      );
      if (typeof success === 'function') {
        success();
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.notifyError(error);
    }
  },

  resetToOpenVersion(subtemplateId: string): void {
    const SubtemplateStore = require('notebook/entry-templates/SubtemplateStore').default;
    const {isLatest} = SubtemplateStore.getEditorState(subtemplateId);
    assert(!isLatest, 'Unexpected reset version while on latest version');
    context.using({immediateAutoSaverQueueSaveAction: ACTION_RESET_VERSION}, this, () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.SUBTEMPLATE_RESET_TO_OPEN_VERSION,
        subtemplateId,
      });
    });
  },

  /**
   * Save the open sub-template version via a throttled request.
   *
   * @param  {String} subtemplateId
   */
  save(subtemplateId: string): JQueryPromise<boolean> {
    function getData(): {openVersion: number; serverSubtemplate: ToServerSubtemplate} {
      const {subtemplate, openVersion, metadata} = SubtemplateStore.getEditorState(subtemplateId);
      const serverSubtemplate = {...SubtemplateStore.prepareForSave(subtemplateId, subtemplate), metadata};
      return {openVersion, serverSubtemplate};
    }

    // This is saved here in case the sub-template is removed from store before save finishes.
    // TODO(T1847): remove after adding refcounts
    const dataAtSaveTime = getData();

    // Create a deferred promise that will be resolved once the save has completed.
    const deferredPromise = $.Deferred<boolean>();
    benchlingUtil.asyncThrottle(`subtemplate-save-${subtemplateId}`, () => {
      let dataToUse;
      if (SubtemplateStore.inStore(subtemplateId)) {
        if (!SubtemplateStore.hasUnsavedChanges(subtemplateId)) {
          return deferredPromise.resolve(false).promise();
        }
        dataToUse = getData();
      } else {
        dataToUse = dataAtSaveTime;
      }

      const {openVersion, serverSubtemplate} = dataToUse;
      AppDispatcher.dispatch({
        actionType: EventConstants.SUBTEMPLATE_SAVE,
        subtemplateId,
      });
      const xhr = SubtemplateDAO.update(subtemplateId, serverSubtemplate).then(
        (response: any) => {
          const versionId = response.cidIdMap[serverSubtemplate.subtemplateVersion.cid];
          assert(typeof versionId === 'number', 'Expected subtemplate version ID to be numeric');
          AppDispatcher.dispatch({
            actionType: EventConstants.SUBTEMPLATE_SAVE_COMPLETED,
            subtemplateId,
            savedVersion: openVersion,
            versionId,
            cidIdMap: response.cidIdMap,
            cidNotePartProvenanceIdMap: response.cidNotePartProvenanceIdMap,
            serverData: {
              editURL: response.editURL,
              templateCollection: response.templateCollection,
            },
          });
          if (SubtemplateStore.inStore(subtemplateId)) {
            SubtemplateActions.fetchVersions(subtemplateId);
          }
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        },
        (response: Response) => {
          const friendlyMessage = notifications.getFriendlyError(response);
          if (friendlyMessage != null) {
            notifications.error(friendlyMessage, {id: 'subtemplate-save-fail', duration: 0});
          }
          AppDispatcher.dispatch({
            actionType: EventConstants.SUBTEMPLATE_SAVE_FAILED,
            subtemplateId,
            error: response,
          });
          // Resolve the deferred promise to signal that the save has failed.
          deferredPromise.resolve(false);
        }
      );
      return asJQueryPromise(xhr);
    });
    // Return the deferred promise so that the caller can wait for the save to complete.
    return deferredPromise.promise();
  },

  // TODO(T1135): Don't pass in deferred. Note: Only ProxyFile should be passing in deferred.
  open(
    subtemplateId: string,
    param: {versionId?: number | string | null; deferred?: JQueryDeferred<unknown> | null}
  ): void {
    let xhrSubtemplate;
    if (param == null) {
      param = {versionId: null, deferred: null};
    }
    const {versionId, deferred} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_OPEN,
      subtemplateId,
    });
    if (versionId != null) {
      xhrSubtemplate = SubtemplateDAO.getVersion(subtemplateId, versionId);
    } else {
      xhrSubtemplate = SubtemplateDAO.get(subtemplateId);
    }

    const xhrTemplateCollection = xhrSubtemplate.then((response: any) => {
      const {templateCollection} = response;
      return SubtemplateDAO.fetchCollection(templateCollection.id);
    });

    Promise.all([xhrSubtemplate, xhrTemplateCollection]).then(
      ([subtemplate, templateCollection]) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SUBTEMPLATE_OPEN_COMPLETED,
          subtemplateId,
          subtemplate,
          templateCollection,
        });
        SubtemplateActions.fetchVersions(subtemplateId);
        if (deferred != null) {
          deferred.resolve();
        }
      },
      (response: Response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SUBTEMPLATE_OPEN_FAILED,
          subtemplateId,
          error: response,
        });
        if (deferred != null) {
          deferred.reject(response);
        }
      }
    );
  },

  close(subtemplateId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.SUBTEMPLATE_CLOSE,
      subtemplateId,
    });
  },
};

export default SubtemplateActions;
